import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AttemptController } from "./AttemptController";
import { FULL_ACCESS_CONTROLS, RECOMMENDED_CHILD_CONTROLS } from "./parentDefaults";
import { ParentSessionController } from "./ParentSessionController";
import { PinVerificationCoordinator } from "./PinVerificationCoordinator";
import {
  PARENT_SETTINGS_STORAGE_KEY,
  readParentSettings,
  removeParentSettings,
  writeParentSettings,
} from "./parentStorage";
import type {
  ChildControls,
  ParentCapability,
  ParentPinRecord,
  ParentSettingsV1,
  ParentStorageState,
  UnlockResult,
} from "./parentTypes";
import { validateChildControls, validateParentSettings } from "./parentValidation";
import { effectiveStudioToolLevel, hasParentCapability } from "./permissionPolicy";
import { ParentModeContext, type ParentModeContextValue } from "./ParentModeContext";

function initialStorageState(): ParentStorageState {
  if (typeof window === "undefined") return { kind: "unconfigured" };
  return readParentSettings(browserStorage("localStorage"));
}

function browserStorage(kind: "localStorage" | "sessionStorage"): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window[kind];
  } catch {
    return null;
  }
}

function controlsForStorageState(storageState: ParentStorageState): ChildControls {
  if (storageState.kind === "valid") return { ...storageState.settings.controls };
  if (storageState.kind === "corrupt") return { ...RECOMMENDED_CHILD_CONTROLS };
  return { ...FULL_ACCESS_CONTROLS };
}

export function ParentModeProvider({ children }: { children: ReactNode }) {
  const [storageState, setStorageState] = useState<ParentStorageState>(initialStorageState);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [notice, setNotice] = useState("");
  const sessionRef = useRef<ParentSessionController | null>(null);
  const verificationRef = useRef<PinVerificationCoordinator | null>(null);
  const attempts = useMemo(() => new AttemptController(browserStorage("localStorage")), []);
  const policyRef = useRef({ controls: controlsForStorageState(storageState), isUnlocked: false });
  const applyStorageState = useCallback((next: ParentStorageState): void => {
    policyRef.current.controls = controlsForStorageState(next);
    setStorageState(next);
  }, []);
  const applyUnlocked = useCallback((next: boolean): void => {
    policyRef.current.isUnlocked = next;
    setIsUnlocked(next);
  }, []);

  useEffect(() => {
    let active = true;
    const verification = new PinVerificationCoordinator({
      attempts,
      readSettings: () => readParentSettings(browserStorage("localStorage")),
      isDocumentHidden: () => document.visibilityState === "hidden",
    });
    verificationRef.current = verification;
    const session = new ParentSessionController(() => {
      verification.invalidate();
      if (active) applyUnlocked(false);
    });
    sessionRef.current = session;
    const recordActivity = (): void => session.recordActivity();
    const handleVisibility = (): void => {
      if (document.visibilityState === "hidden") verification.invalidate();
      session.handleVisibility(document.visibilityState === "hidden");
    };
    const handlePageHide = (): void => {
      verification.invalidate();
      session.handlePageHide();
    };
    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== null && event.key !== PARENT_SETTINGS_STORAGE_KEY) return;
      verification.invalidate();
      session.lock("manual");
      applyStorageState(readParentSettings(browserStorage("localStorage")));
    };
    window.addEventListener("pointerdown", recordActivity);
    window.addEventListener("keydown", recordActivity);
    window.addEventListener("touchstart", recordActivity, { passive: true });
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      active = false;
      window.removeEventListener("pointerdown", recordActivity);
      window.removeEventListener("keydown", recordActivity);
      window.removeEventListener("touchstart", recordActivity);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
      verification.destroy();
      if (verificationRef.current === verification) verificationRef.current = null;
      session.destroy();
      sessionRef.current = null;
    };
  }, [applyStorageState, applyUnlocked, attempts]);

  const controls = useMemo<ChildControls>(
    () => controlsForStorageState(storageState),
    [storageState],
  );

  const can = useCallback(
    (capability: ParentCapability): boolean =>
      hasParentCapability(policyRef.current.controls, policyRef.current.isUnlocked, capability),
    [],
  );
  const clearNotice = useCallback((): void => setNotice(""), []);
  const askParent = useCallback(
    (message = "Ask a parent to unlock this feature."): void => setNotice(message),
    [],
  );

  const unlock = useCallback(
    async (pin: string): Promise<UnlockResult> => {
      if (storageState.kind !== "valid") return { kind: "unavailable" };
      const verification = verificationRef.current;
      if (!verification) return { kind: "unavailable" };
      const result = await verification.verify(pin, storageState.settings);
      if (result.kind !== "success") return result;
      applyUnlocked(true);
      sessionRef.current?.unlock();
      return { kind: "success" };
    },
    [applyUnlocked, storageState],
  );

  const lock = useCallback((): void => {
    verificationRef.current?.invalidate();
    sessionRef.current?.lock("manual");
    applyUnlocked(false);
  }, [applyUnlocked]);

  const saveInitialConfiguration = useCallback(
    (pin: ParentPinRecord, nextControls: ChildControls): void => {
      verificationRef.current?.invalidate();
      const settings = validateParentSettings({ version: 1, pin, controls: nextControls });
      writeParentSettings(browserStorage("localStorage"), settings);
      attempts.reset();
      applyStorageState({ kind: "valid", settings });
      applyUnlocked(true);
      sessionRef.current?.unlock();
    },
    [applyStorageState, applyUnlocked, attempts],
  );

  const saveControls = useCallback(
    (nextControls: ChildControls): void => {
      verificationRef.current?.invalidate();
      if (storageState.kind !== "valid" || !isUnlocked || !sessionRef.current?.isUnlocked()) {
        throw new Error("Parent Mode must be unlocked.");
      }
      const settings: ParentSettingsV1 = {
        version: 1,
        pin: { ...storageState.settings.pin },
        controls: validateChildControls(nextControls),
      };
      writeParentSettings(browserStorage("localStorage"), settings);
      applyStorageState({ kind: "valid", settings });
      sessionRef.current?.recordActivity();
    },
    [applyStorageState, isUnlocked, storageState],
  );

  const saveChangedPin = useCallback(
    (pin: ParentPinRecord): void => {
      verificationRef.current?.invalidate();
      if (storageState.kind !== "valid" || !isUnlocked || !sessionRef.current?.isUnlocked()) {
        throw new Error("Parent Mode must be unlocked.");
      }
      const settings = validateParentSettings({
        version: 1,
        pin,
        controls: storageState.settings.controls,
      });
      writeParentSettings(browserStorage("localStorage"), settings);
      applyStorageState({ kind: "valid", settings });
      attempts.reset();
      sessionRef.current?.recordActivity();
    },
    [applyStorageState, attempts, isUnlocked, storageState],
  );

  const resetConfiguration = useCallback(
    (confirmation: string): void => {
      if (confirmation !== "RESET PARENT MODE") {
        throw new Error("Type RESET PARENT MODE to confirm.");
      }
      verificationRef.current?.invalidate();
      removeParentSettings(browserStorage("localStorage"));
      attempts.reset();
      lock();
      applyStorageState({ kind: "unconfigured" });
    },
    [applyStorageState, attempts, lock],
  );

  const value = useMemo<ParentModeContextValue>(
    () => ({
      storageState,
      controls,
      isConfigured: storageState.kind === "valid",
      isCorrupt: storageState.kind === "corrupt",
      isUnlocked,
      toolLevel: effectiveStudioToolLevel(controls, isUnlocked),
      notice,
      clearNotice,
      askParent,
      can,
      getAttemptStatus: () => attempts.getStatus(),
      unlock,
      lock,
      saveInitialConfiguration,
      saveControls,
      saveChangedPin,
      resetConfiguration,
    }),
    [
      attempts,
      askParent,
      can,
      clearNotice,
      controls,
      isUnlocked,
      lock,
      notice,
      resetConfiguration,
      saveChangedPin,
      saveControls,
      saveInitialConfiguration,
      storageState,
      unlock,
    ],
  );

  return <ParentModeContext.Provider value={value}>{children}</ParentModeContext.Provider>;
}
